import React, { useState, useMemo } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_HEADERS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const fmt = (n) => (n < 0 ? '-$' : '$') + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }

// ─── Core accrual math ────────────────────────────────────────────────────────

function itemHitsOnDate(item, year, month, day) {
  const date = new Date(year, month, day);
  const lastOcc = item.lastOccurrence ? new Date(item.lastOccurrence + 'T00:00:00') : null;
  switch (item.frequency) {
    case 'Daily': return true;
    case 'Weekly': {
      if (lastOcc) { const diff = Math.round((date - lastOcc) / 86400000); return diff % 7 === 0; }
      return date.getDay() === 1;
    }
    case 'Biweekly': {
      if (lastOcc) { const diff = Math.round((date - lastOcc) / 86400000); return diff % 14 === 0; }
      const anchor = new Date(year, 0, 1);
      while (anchor.getDay() !== 1) anchor.setDate(anchor.getDate() + 1);
      const diff = Math.round((date - anchor) / 86400000);
      return date.getDay() === 1 && diff % 14 === 0;
    }
    case 'Monthly':
      if (lastOcc) return day === lastOcc.getDate();
      return day === (item.dayOfMonth || 1);
    case 'Quarterly': {
      if (lastOcc) {
        const md = (year - lastOcc.getFullYear()) * 12 + (month - lastOcc.getMonth());
        return md % 3 === 0 && day === lastOcc.getDate();
      }
      return day === (item.dayOfMonth || 1) && [0,3,6,9].includes(month);
    }
    case 'Semi-annually': {
      if (lastOcc) {
        const md = (year - lastOcc.getFullYear()) * 12 + (month - lastOcc.getMonth());
        return md % 6 === 0 && day === lastOcc.getDate();
      }
      return day === (item.dayOfMonth || 1) && [0,6].includes(month);
    }
    case 'Yearly':
      if (lastOcc) return month === lastOcc.getMonth() && day === lastOcc.getDate();
      return day === (item.dayOfMonth || 1) && month === 0;
    default: return false;
  }
}

function findLastHit(item, date) {
  if (item.frequency === 'Daily') return new Date(date);
  const cursor = new Date(date);
  for (let i = 0; i < 400; i++) {
    if (itemHitsOnDate(item, cursor.getFullYear(), cursor.getMonth(), cursor.getDate())) return new Date(cursor);
    cursor.setDate(cursor.getDate() - 1);
  }
  return new Date(date);
}

function findNextHit(item, lastHit) {
  if (item.frequency === 'Daily') {
    const next = new Date(lastHit); next.setDate(next.getDate() + 1); return next;
  }
  const cursor = new Date(lastHit); cursor.setDate(cursor.getDate() + 1);
  for (let i = 0; i < 400; i++) {
    if (itemHitsOnDate(item, cursor.getFullYear(), cursor.getMonth(), cursor.getDate())) return new Date(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  const fallback = new Date(lastHit); fallback.setDate(fallback.getDate() + 30); return fallback;
}

function getAccrualInfo(item, date) {
  if (item.frequency === 'Daily') {
    const next = new Date(date); next.setDate(next.getDate() + 1);
    return { pct: 1, reserved: item.amount, period: 1, elapsed: 1, daysUntilNext: 0, lastHit: new Date(date), nextHit: next, isDaily: true };
  }
  const lastHit = findLastHit(item, date);
  const nextHit = findNextHit(item, lastHit);
  const period  = Math.round((nextHit - lastHit) / 86400000);
  const elapsed = Math.round((date - lastHit) / 86400000);
  const pct     = period > 0 ? Math.min(elapsed / period, 1) : 0;
  return { pct, reserved: pct * item.amount, period, elapsed, daysUntilNext: Math.round((nextHit - date) / 86400000), lastHit, nextHit, isDaily: false };
}

function countHitsBetween(item, startDate, endDate) {
  if (item.frequency === 'Daily') return Math.max(0, Math.round((endDate - startDate) / 86400000));
  let count = 0;
  const cursor = new Date(startDate); cursor.setDate(cursor.getDate() + 1);
  while (cursor <= endDate) {
    if (itemHitsOnDate(item, cursor.getFullYear(), cursor.getMonth(), cursor.getDate())) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

function getExpectedBalance(targetDate, todayDate, todayBalance, items) {
  const isFuture = targetDate > todayDate;
  const isPast   = targetDate < todayDate;
  if (!isFuture && !isPast) return todayBalance;

  let balance = todayBalance;
  for (const item of items) {
    const sign     = item.type === 'income' ? 1 : -1;
    const hitCount = isFuture
      ? countHitsBetween(item, todayDate, targetDate)
      : countHitsBetween(item, targetDate, todayDate);
    balance += (isFuture ? 1 : -1) * hitCount * item.amount * sign;
  }
  return balance;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ForecastView({ budgetItems, endOfDayBalance, isPro, currency }) {
  const bp = useBreakpoint();
  const isMobile  = bp === 'mobile';
  const isTablet  = bp === 'tablet';
  const isDesktop = bp === 'desktop';
  const { t, isDark, friendlyMode } = useTheme();

  const todayObj = new Date(); todayObj.setHours(0, 0, 0, 0);

  const [displayYear,  setDisplayYear]  = useState(todayObj.getFullYear());
  const [displayMonth, setDisplayMonth] = useState(todayObj.getMonth());
  const [selectedDay,  setSelectedDay]  = useState(todayObj.getDate());
  const [showNavPrompt, setShowNavPrompt] = useState(false);

  const prevMonth = () => {
    if (!isPro) { setShowNavPrompt(true); return; }
    if (displayMonth === 0) { setDisplayYear((y) => y - 1); setDisplayMonth(11); }
    else setDisplayMonth((m) => m - 1);
  };
  const nextMonth = () => {
    // Limit forward navigation: free = current + 1, Pro = current + 12
    const maxFwdMonths = isPro ? 12 : 1;
    const diffMonths = (displayYear - todayObj.getFullYear()) * 12 + (displayMonth - todayObj.getMonth());
    if (diffMonths >= maxFwdMonths) { if (!isPro) setShowNavPrompt(true); return; }
    if (displayMonth === 11) { setDisplayYear((y) => y + 1); setDisplayMonth(0); }
    else setDisplayMonth((m) => m + 1);
  };

  const isToday = (day) => day === todayObj.getDate() && displayMonth === todayObj.getMonth() && displayYear === todayObj.getFullYear();

  // Calendar grid
  const daysInMonth = getDaysInMonth(displayYear, displayMonth);
  const firstDay    = getFirstDayOfMonth(displayYear, displayMonth);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  // Pre-compute expected balances for every day in the displayed month
  const balanceMap = useMemo(() => {
    const map = {};
    for (let d = 1; d <= daysInMonth; d++) {
      const target = new Date(displayYear, displayMonth, d); target.setHours(0, 0, 0, 0);
      map[d] = getExpectedBalance(target, todayObj, endOfDayBalance, budgetItems);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayYear, displayMonth, daysInMonth, endOfDayBalance, JSON.stringify(budgetItems)]);

  // Accrual breakdown for the selected day
  const selectedAccrualDetails = useMemo(() => {
    if (!selectedDay) return null;
    const date = new Date(displayYear, displayMonth, selectedDay); date.setHours(0, 0, 0, 0);
    return budgetItems.map((item) => ({ item, ...getAccrualInfo(item, date) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, displayYear, displayMonth, JSON.stringify(budgetItems)]);

  // Responsive
  const pagePadding       = isDesktop ? '20px' : isTablet ? '16px' : '12px';
  const layoutDirection   = isMobile ? 'column' : 'row';
  const sidebarWidth      = isMobile ? 'auto' : isTablet ? '240px' : '320px';
  const sidebarFlexShrink = isMobile ? 1 : 0;
  const cellMinHeight     = isDesktop ? '90px' : isTablet ? '70px' : '52px';
  const cellPadding       = isMobile ? '4px' : '6px';
  const showSingleLetter  = isMobile || isTablet;

  return (
    <div style={{ minHeight: '100vh', background: t.pageBg, padding: pagePadding }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', maxWidth: '1400px', margin: '0 auto 20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? '17px' : isTablet ? '19px' : '22px', fontWeight: '700', color: t.text, lineHeight: '1.25' }}>
            {friendlyMode ? 'Real Balance — What You Actually Have' : 'Accrual Forecast'}
          </h1>
          <div style={{ fontSize: isMobile ? '12px' : '13px', color: t.textMuted, marginTop: '3px', lineHeight: '1.4' }}>
            {friendlyMode
              ? "Your bank balance minus what's already spoken for. This is what you can actually spend right now."
              : 'Expected balance based on how far through each billing period you are'
            }
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
          <span style={{ fontSize: '12px', color: t.textMuted }}>Today's Balance</span>
          <span style={{ fontSize: '22px', fontWeight: '700', color: endOfDayBalance >= 0 ? (isDark ? '#81c784' : '#1b6d24') : (isDark ? '#ef9a9a' : '#ba1a1a') }}>
            {fmt(endOfDayBalance)}
          </span>
        </div>
      </div>

      {/* Layout */}
      <div style={{ display: 'flex', flexDirection: layoutDirection, gap: '20px', alignItems: 'flex-start', maxWidth: '1400px', margin: '0 auto' }}>

        {/* Calendar */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '16px' }}>
            <button style={st.navBtn} onClick={prevMonth}>‹</button>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', minWidth: '180px', textAlign: 'center', color: t.text }}>
              {MONTHS[displayMonth]} {displayYear}
            </h2>
            <button style={st.navBtn} onClick={nextMonth}>›</button>
          </div>

          {/* Free-tier nav prompt */}
          {showNavPrompt && (
            <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: t.textMuted }}>Upgrade to Pro to navigate to other months.</span>
              <button onClick={() => setShowNavPrompt(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textFaintest, fontSize: '16px', padding: '0 4px', fontFamily: 'inherit', lineHeight: 1 }}>✕</button>
            </div>
          )}

          {/* Grid — single container so all columns share the same track widths */}
          <div style={{ background: t.card, borderRadius: '8px', boxShadow: t.shadow, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>

              {/* Header row */}
              {DAY_HEADERS.map((d) => (
                <div key={d} style={{ background: t.tableSubHeader, color: t.textSub, padding: isMobile ? '6px 2px' : '10px', textAlign: 'center', fontSize: isMobile ? '11px' : '14px', fontWeight: '600', borderRight: `1px solid ${t.borderLight}`, borderBottom: `1px solid ${t.borderLight}`, boxSizing: 'border-box' }}>
                  {showSingleLetter ? d[0] : d}
                </div>
              ))}

              {/* Day cells */}
              {cells.map((day, i) => {
                if (!day) {
                  return <div key={i} style={{ minHeight: cellMinHeight, background: t.tableRowAlt, borderRight: `1px solid ${t.borderLight}`, borderBottom: `1px solid ${t.borderLight}`, boxSizing: 'border-box' }} />;
                }

                const bal        = balanceMap[day] ?? endOfDayBalance;
                const isPos      = bal >= 0;
                const todayCell  = isToday(day);
                const isSelected = selectedDay === day;

                const hitsToday = budgetItems.filter(
                  (item) => item.frequency !== 'Daily' && itemHitsOnDate(item, displayYear, displayMonth, day)
                );

                const chipBg    = isPos ? (isDark ? '#1a3d20' : 'rgba(27,109,36,0.1)')  : (isDark ? '#3d1a1a' : 'rgba(186,26,26,0.08)');
                const chipColor = isPos ? (isDark ? '#81c784' : '#1b6d24')  : (isDark ? '#ef9a9a' : '#ba1a1a');

                return (
                  <div
                    key={i}
                    onClick={() => setSelectedDay(day)}
                    style={{
                      minHeight: cellMinHeight,
                      padding: cellPadding,
                      borderRight: `1px solid ${t.borderLight}`,
                      borderBottom: `1px solid ${t.borderLight}`,
                      background: t.card,
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                      ...(todayCell  ? { outline: '2px solid #0061a4', outlineOffset: '-2px', background: isDark ? '#07253f' : 'rgba(0,97,164,0.05)' } : {}),
                      ...(isSelected && !todayCell ? { outline: `2px solid ${isDark ? '#8b949e' : '#bdbdbd'}`, outlineOffset: '-2px', background: isDark ? '#1c2128' : '#f5f5f5' } : {}),
                    }}
                  >
                    {/* Day number + hit dots */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <span style={{ fontSize: isMobile ? '11px' : '13px', fontWeight: '500', color: t.textSub }}>{day}</span>
                      <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {hitsToday.slice(0, 4).map((item) => (
                          <span key={item.id} title={item.name} style={{ display: 'inline-block', width: isMobile ? '5px' : '7px', height: isMobile ? '5px' : '7px', borderRadius: '50%', background: item.type === 'income' ? (isDark ? '#66bb6a' : '#1b6d24') : (isDark ? '#ef9a9a' : '#ba1a1a'), flexShrink: 0 }} />
                        ))}
                        {hitsToday.length > 4 && <span style={{ fontSize: '8px', color: t.textMuted }}>+{hitsToday.length - 4}</span>}
                      </div>
                    </div>

                    {/* Balance chip */}
                    <div style={{ borderRadius: '10px', padding: isMobile ? '2px 3px' : '3px 6px', background: chipBg, color: chipColor, fontSize: isMobile ? '10px' : isTablet ? '11px' : '12px', fontWeight: '600', textAlign: 'center', display: 'block', width: '100%', boxSizing: 'border-box' }}>
                      {fmt(bal)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginTop: '14px', flexWrap: 'wrap' }}>
            <LegendDot color={isDark ? '#66bb6a' : '#1b6d24'} label="Income hit" />
            <LegendDot color={isDark ? '#ef9a9a' : '#ba1a1a'} label="Expense hit" />
            <LegendSwatch bg={isDark ? '#1a3d20' : '#e8f5e9'} border={isDark ? '#1b6d24' : '#c8e6c9'} label="Positive" />
            <LegendSwatch bg={isDark ? '#3d1a1a' : '#ffebee'} border={isDark ? '#ba1a1a' : '#ffcdd2'} label="Negative" />
            <LegendSwatch bg={isDark ? '#07253f' : '#bbdefb'} border="#0061a4" label="Today" />
          </div>
        </div>

        {/* Detail sidebar */}
        <div style={{ width: sidebarWidth, flexShrink: sidebarFlexShrink, background: t.card, borderRadius: '8px', boxShadow: t.shadow, overflow: 'hidden' }}>
          <DetailPanel
            selectedDay={selectedDay}
            displayYear={displayYear}
            displayMonth={displayMonth}
            expectedBalance={selectedDay ? balanceMap[selectedDay] : null}
            accrualDetails={selectedAccrualDetails}
            t={t}
            isDark={isDark}
            isMobile={isMobile}
            friendlyMode={friendlyMode}
          />
        </div>

      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

function DetailPanel({ selectedDay, displayYear, displayMonth, expectedBalance, accrualDetails, t, isDark, isMobile, friendlyMode }) {
  if (!selectedDay || !accrualDetails) {
    return (
      <div style={{ padding: '24px 16px', textAlign: 'center' }}>
        <p style={{ color: t.textMuted, fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
          Click a day on the calendar to see the accrual breakdown for each item.
        </p>
      </div>
    );
  }

  const dateLabel = new Date(displayYear, displayMonth, selectedDay).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
  const isPos = (expectedBalance ?? 0) >= 0;

  let totalIncome = 0, totalExpenses = 0;
  for (const { item, reserved } of accrualDetails) {
    if (item.type === 'income') totalIncome += reserved;
    else totalExpenses += reserved;
  }
  const net = totalIncome - totalExpenses;

  // Sort: income first, then expense; by reserved amount descending
  const sorted = [...accrualDetails].sort((a, b) => {
    if (a.item.type !== b.item.type) return a.item.type === 'income' ? -1 : 1;
    return b.reserved - a.reserved;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>

      {/* Date + balance header */}
      <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${t.borderLight}` }}>
        <div style={{ fontSize: '12px', color: t.textMuted, marginBottom: '4px' }}>{dateLabel}</div>
        <div style={{ fontSize: '28px', fontWeight: '700', color: isPos ? (isDark ? '#81c784' : '#1b6d24') : (isDark ? '#ef9a9a' : '#ba1a1a') }}>
          {fmt(expectedBalance ?? 0)}
        </div>
        <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '2px' }}>Expected balance</div>
      </div>

      {/* Per-item list */}
      <div style={{ overflowY: 'auto', maxHeight: isMobile ? '340px' : '440px' }}>
        {sorted.length === 0 && (
          <p style={{ padding: '16px', color: t.textMuted, fontSize: '13px', textAlign: 'center' }}>
            No budget items configured yet.
          </p>
        )}

        {sorted.map(({ item, pct, reserved, period, elapsed, daysUntilNext, lastHit, nextHit, isDaily }) => {
          const isIncome = item.type === 'income';
          const dotColor  = isIncome ? (isDark ? '#66bb6a' : '#1b6d24') : (isDark ? '#ef9a9a' : '#ba1a1a');
          const barColor  = isIncome ? (isDark ? '#1b6d24' : '#a5d6a7') : (isDark ? '#ba1a1a' : '#ef9a9a');
          const amtColor  = isIncome ? (isDark ? '#81c784' : '#1b6d24') : (isDark ? '#ef9a9a' : '#ba1a1a');
          const pctDisplay = Math.round(pct * 100);

          return (
            <div key={item.id} style={{ padding: '11px 16px', borderBottom: `1px solid ${t.borderLight}` }}>

              {/* Name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '7px' }}>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', background: dotColor, flexShrink: 0 }} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: t.textSub, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name}
                </span>
                {isDaily && (
                  <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '10px', background: isDark ? '#1c2128' : '#d1e4ff', color: isDark ? '#79c0ff' : '#0061a4', flexShrink: 0 }}>
                    Daily
                  </span>
                )}
              </div>

              {/* Progress bar */}
              <div style={{ height: '6px', borderRadius: '3px', background: isDark ? '#30363d' : '#e0e0e0', marginBottom: '6px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pctDisplay}%`, borderRadius: '3px', background: barColor }} />
              </div>

              {/* % + reserved */}
              <div style={{ fontSize: '12px', color: t.textSecondary, marginBottom: isDaily ? 0 : '3px' }}>
                <strong style={{ color: t.text }}>{pctDisplay}%</strong> through period{' — '}
                <strong style={{ color: amtColor }}>{fmt(reserved)}</strong>{' '}
                {isIncome ? 'earned' : (friendlyMode ? 'spoken for' : 'reserved')}
              </div>

              {/* Last / next */}
              {!isDaily && lastHit && nextHit && (
                <div style={{ fontSize: '11px', color: t.textMuted }}>
                  Last: {fmtDate(lastHit)} · Next: {fmtDate(nextHit)} ({daysUntilNext}d)
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary footer */}
      <div style={{ padding: '12px 16px', borderTop: `1px solid ${t.borderMid}`, background: isDark ? '#0d1117' : '#fafafa' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
          {friendlyMode ? 'Summary' : 'Accrual Summary'}
        </div>
        <SRow label={friendlyMode ? 'Income built up' : 'Accrued income'}   value={fmt(totalIncome)}    color={isDark ? '#81c784' : '#1b6d24'} t={t} />
        <SRow label={friendlyMode ? 'Expenses spoken for' : 'Accrued expenses'} value={fmt(totalExpenses)}  color={isDark ? '#ef9a9a' : '#ba1a1a'} t={t} />
        <div style={{ borderTop: `1px solid ${t.borderLight}`, marginTop: '6px', paddingTop: '6px' }}>
          <SRow label={friendlyMode ? 'Net amount' : 'Net accrued'} value={fmt(net)} color={net >= 0 ? (isDark ? '#81c784' : '#1b6d24') : (isDark ? '#ef9a9a' : '#ba1a1a')} t={t} bold />
        </div>
      </div>
    </div>
  );
}

function SRow({ label, value, color, t, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
      <span style={{ fontSize: '12px', color: t.textMuted }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: bold ? '700' : '500', color }}>{value}</span>
    </div>
  );
}

function LegendDot({ color, label }) {
  const { t } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: color }} />
      <span style={{ color: t.textTertiary, fontSize: '13px' }}>{label}</span>
    </div>
  );
}

function LegendSwatch({ bg, border, label }) {
  const { t } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <div style={{ width: '28px', height: '14px', borderRadius: '3px', background: bg, border: `1px solid ${border}` }} />
      <span style={{ color: t.textTertiary, fontSize: '13px' }}>{label}</span>
    </div>
  );
}

const st = {
  navBtn: {
    background: '#0061a4', color: 'white', border: 'none', borderRadius: '8px',
    width: '36px', height: '36px', cursor: 'pointer', fontSize: '20px', fontWeight: '700',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'inherit', lineHeight: 1,
  },
};
